import { Injectable, signal } from '@angular/core';
import { AppRoles } from '@app/app.role';
import { Account } from '@shared/models/account.model';
import { AssignedPrivileges } from '@shared/models/assigned-privileges.model';

export interface State {
  permissions: [];
  privileges: AssignedPrivileges;
  principal: Account | null;
  accountEmail: string;
  authenticated: boolean;
  availableLanguages: string[];
  availableRoles: string[];
}

const initialState: State = {
  permissions: [],
  principal: null,
  privileges: null,
  authenticated: false,
  accountEmail: null,
  availableLanguages: [],
  availableRoles: []
};

@Injectable({
  providedIn: 'root'
})
export class AppState {
  protected state = signal<State>(initialState);

  get accountEmail(): string {
    return this.state().accountEmail;
  }

  set accountEmail(value: string) {
    this.state.set({
      ...this.state(),
      accountEmail: value
    });
  }

  get authenticated(): boolean {
    return this.state().authenticated;
  }

  set authenticated(value: boolean) {
    this.state.set({
      ...this.state(),
      authenticated: value
    });
  }

  get privileges(): AssignedPrivileges | null {
    return this.state().privileges;
  }

  set privileges(value: AssignedPrivileges | null) {
    this.state.set({
      ...this.state(),
      privileges: value
    });
  }

  get principal(): Account | null {
    return this.state().principal;
  }

  set principal(value: Account | null) {
    this.state.set({
      ...this.state(),
      principal: value
    });
  }

  get availableLanguages(): string[] | [] {
    return this.state().availableLanguages;
  }

  get isLocalFinanceAdmin(): boolean {
    return [
      AppRoles.SUPER_ADMIN,
      AppRoles.LOCAL_FINANCE_ADMIN,
      AppRoles.DISTRICT_FINANCE_ADMIN,
      AppRoles.AREA_FINANCE_ADMIN,
      AppRoles.NATIONAL_FINANCE_ADMIN
    ].some((role) => this.state().principal?.authorities?.includes(role));
  }

  get isDistrictFinanceAdmin(): boolean {
    return [
      AppRoles.SUPER_ADMIN,
      AppRoles.DISTRICT_FINANCE_ADMIN,
      AppRoles.AREA_FINANCE_ADMIN,
      AppRoles.NATIONAL_FINANCE_ADMIN
    ].some((role) => this.state().principal?.authorities?.includes(role));
  }

  get isManager(): boolean {
    return [AppRoles.SUPER_ADMIN, AppRoles.MANAGER].some((role) =>
      this.state().principal?.authorities?.includes(role)
    );
  }

  get isSuperAdmin(): boolean {
    return this.state().principal?.authorities?.includes(AppRoles.SUPER_ADMIN);
  }

  set availableLanguages(value: string[] | null) {
    this.state.set({
      ...this.state(),
      availableLanguages: value ?? []
    });
  }
}
